import { CloseOutlined, MinusOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ImagePreviewModalProps {
  src: string;
  alt: string;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const ZOOM_STEP = 0.3;

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ src, alt, onClose }) => {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const clampTranslate = useCallback((x: number, y: number, s: number) => {
    const el = containerRef.current;
    if (!el) return { x, y };
    const rect = el.getBoundingClientRect();
    const maxX = (rect.width * (s - 1)) / 2;
    const maxY = (rect.height * (s - 1)) / 2;
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, []);

  const reset = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  const zoomIn = () =>
    setScale(s => {
      const next = Math.min(MAX_SCALE, +(s + ZOOM_STEP).toFixed(2));
      setTranslate(t => clampTranslate(t.x, t.y, next));
      return next;
    });

  const zoomOut = () =>
    setScale(s => {
      const next = Math.max(MIN_SCALE, +(s - ZOOM_STEP).toFixed(2));
      if (next === MIN_SCALE) setTranslate({ x: 0, y: 0 });
      else setTranslate(t => clampTranslate(t.x, t.y, next));
      return next;
    });

  // Mouse wheel zoom — zoom toward cursor position
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setScale(prev => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, +(prev + delta).toFixed(2)));
      if (next === MIN_SCALE) setTranslate({ x: 0, y: 0 });
      else setTranslate(t => clampTranslate(t.x, t.y, next));
      return next;
    });
  };

  // Mouse drag pan
  const onMouseDown = (e: React.MouseEvent) => {
    if (scale <= 1) return;
    e.preventDefault();
    isDragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setTranslate(t => clampTranslate(t.x + dx, t.y + dy, scale));
  };

  const onMouseUp = () => { isDragging.current = false; };

  // Touch drag pan
  const lastTouch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && scale > 1) {
      lastTouch.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && lastTouch.current && scale > 1) {
      const dx = e.touches[0]!.clientX - lastTouch.current.x;
      const dy = e.touches[0]!.clientY - lastTouch.current.y;
      lastTouch.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
      setTranslate(t => clampTranslate(t.x + dx, t.y + dy, scale));
    }
  };

  const onTouchEnd = () => { lastTouch.current = null; };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      {/* Modal panel — stop propagation so clicks inside don't close */}
      <div
        className="relative flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ width: '90vw', maxWidth: 900, height: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-neutral-200 shrink-0">
          <span className="text-neutral-800 text-sm font-medium truncate max-w-[70%]">{alt}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 disabled:opacity-40 transition-colors"
              title="Zoom out"
            >
              <MinusOutlined style={{ fontSize: 14 }} />
            </button>
            <span className="text-neutral-600 text-xs w-10 text-center">{Math.round(scale * 100)}%</span>
            <button
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 disabled:opacity-40 transition-colors"
              title="Zoom in"
            >
              <PlusOutlined style={{ fontSize: 14 }} />
            </button>
            <button
              onClick={reset}
              disabled={scale === 1 && translate.x === 0 && translate.y === 0}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 disabled:opacity-40 transition-colors"
              title="Reset"
            >
              <UndoOutlined style={{ fontSize: 14 }} />
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-red-100 text-neutral-700 hover:text-red-600 transition-colors ml-1"
              title="Close"
            >
              <CloseOutlined style={{ fontSize: 14 }} />
            </button>
          </div>
        </div>

        {/* Image canvas — white background so transparent PNGs render correctly */}
        <div
          ref={containerRef}
          className="flex-1 overflow-hidden flex items-center justify-center relative bg-white"
          style={{ cursor: scale > 1 ? (isDragging.current ? 'grabbing' : 'grab') : 'default' }}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onDoubleClick={reset}
        >
          <img
            src={src}
            alt={alt}
            draggable={false}
            style={{
              transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
              transformOrigin: 'center center',
              transition: isDragging.current ? 'none' : 'transform 0.15s ease',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* Hint */}
        <div className="shrink-0 py-2 text-center text-text-100 text-xs bg-white border-t border-neutral-200">
          Scroll to zoom · Drag to pan · Double-click image to reset
        </div>
      </div>
    </div>
  );
};
